import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Lock, User, Eye, EyeOff, Loader2, GraduationCap, ArrowLeft, Clock, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { authRegister, authLogin } from "../lib/api";
import { toast } from "sonner";

export default function AuthPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // "login" or "register"
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [pendingInfo, setPendingInfo] = useState(null); // { email, reason: "registered" | "login_blocked" }
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    role: "teacher"
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === "register") {
        // Validate
        if (formData.password.length < 8) {
          toast.error("Le mot de passe doit contenir au moins 8 caractères");
          setLoading(false);
          return;
        }
        
        const { data } = await authRegister(
          formData.email,
          formData.password,
          formData.name,
          formData.role
        );
        
        // No auto-login: show pending validation screen
        setPendingInfo({ email: data.email, reason: "registered" });
        toast.success("Compte créé. En attente de validation par l'administrateur.");
      } else {
        // Login
        const { data } = await authLogin(formData.email, formData.password);
        
        toast.success("Connexion réussie !");
        localStorage.setItem("navi_user_id", data.id);
        localStorage.setItem("navi_user_email", data.email);
        localStorage.setItem("navi_user_name", data.name);
        localStorage.setItem("navi_user_role", data.role);
        
        // Redirect based on role
        if (data.role === "admin") {
          navigate("/admin");
        } else {
          navigate("/hub");
        }
      }
    } catch (err) {
      console.error("Auth error:", err);
      const status = err.response?.status;
      const message = err.response?.data?.detail || "Une erreur est survenue";
      
      // Account pending validation (403)
      if (status === 403 && mode === "login" && /validation|attente|pending/i.test(message)) {
        setPendingInfo({ email: formData.email, reason: "login_blocked" });
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex flex-col">
      {/* Header */}
      <header className="p-4">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-800">
          <ArrowLeft size={20} />
          <span>Retour</span>
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <GraduationCap className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800">L'École de Navi</h1>
            <p className="text-slate-500 mt-1">
              {pendingInfo
                ? "Compte en attente de validation"
                : mode === "login" ? "Connectez-vous à votre compte" : "Créez votre compte enseignant"}
            </p>
          </div>

          {pendingInfo ? (
            /* Pending Validation Screen */
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100 text-center" data-testid="pending-validation-screen">
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-orange-500" />
              </div>
              <h2 className="text-xl font-bold text-slate-800 mb-3">
                {pendingInfo.reason === "registered"
                  ? "Votre compte a bien été créé !"
                  : "Compte en attente"}
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                {pendingInfo.reason === "registered" ? (
                  <>
                    Merci de votre inscription avec <strong>{pendingInfo.email}</strong>.<br />
                    Un administrateur doit <strong>valider votre compte</strong> avant que vous ne puissiez vous connecter.
                  </>
                ) : (
                  <>
                    Votre compte <strong>{pendingInfo.email}</strong> est en attente de validation par un administrateur.
                    <br />Merci de patienter, vous pourrez vous connecter dès qu'il sera approuvé.
                  </>
                )}
              </p>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-6 text-left">
                <p className="text-sm text-slate-600 flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Vous serez notifié dès que votre compte sera activé. Aucune action n'est requise de votre part pour le moment.</span>
                </p>
              </div>
              <Button
                variant="outline"
                className="w-full h-12"
                onClick={() => {
                  setPendingInfo(null);
                  setMode("login");
                  setFormData({ email: "", password: "", name: "", role: "teacher" });
                }}
                data-testid="pending-back-btn"
              >
                Retour à la connexion
              </Button>
            </div>
          ) : (
          /* Form Card */
          <div className="bg-white rounded-2xl shadow-xl p-6 border border-slate-100">
            {/* Mode Toggle */}
            <div className="flex gap-2 mb-6 p-1 bg-slate-100 rounded-xl">
              <button
                type="button"
                onClick={() => setMode("login")}
                className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition-all ${
                  mode === "login" 
                    ? "bg-white text-slate-800 shadow" 
                    : "text-slate-500 hover:text-slate-700"
                }`}
                data-testid="auth-tab-login"
              >
                Connexion
              </button>
              <button
                type="button"
                onClick={() => setMode("register")}
                className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition-all ${
                  mode === "register" 
                    ? "bg-white text-slate-800 shadow" 
                    : "text-slate-500 hover:text-slate-700"
                }`}
                data-testid="auth-tab-register"
              >
                Inscription
              </button>
            </div>

            {mode === "register" && (
              <div className="mb-4 bg-orange-50 border border-orange-200 rounded-xl p-3 flex items-start gap-2">
                <Clock className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-orange-700">
                  Votre compte sera soumis à <strong>validation par un administrateur</strong> avant de pouvoir accéder à la plateforme.
                </p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name field (register only) */}
              {mode === "register" && (
                <div className="space-y-2">
                  <Label htmlFor="name">Nom complet</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      placeholder="Mme Dupont"
                      value={formData.name}
                      onChange={handleChange}
                      className="pl-10 h-12"
                      data-testid="auth-name-input"
                    />
                  </div>
                </div>
              )}

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="vous@ecole.fr"
                    value={formData.email}
                    onChange={handleChange}
                    className="pl-10 h-12"
                    data-testid="auth-email-input"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    placeholder={mode === "register" ? "8 caractères minimum" : "••••••••"}
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-10 pr-10 h-12"
                    data-testid="auth-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {mode === "register" && (
                  <p className="text-xs text-slate-500">
                    Le mot de passe doit contenir au moins 8 caractères
                  </p>
                )}
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                data-testid="auth-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : mode === "login" ? (
                  "Se connecter"
                ) : (
                  "Créer mon compte"
                )}
              </Button>
            </form>
          </div>
          )}
        </div>
      </main>
    </div>
  );
}
